import { useState } from 'react';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { useSnapshot } from 'valtio';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  InlineRow,
  Input,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Select,
  Stack,
  StatCard,
  StatGrid,
  Strong,
  Table,
  TableWrap,
  TBody,
  TD,
  TDActions,
  TH,
  THead,
  TR,
} from '../../components/ui';
import {
  useAdminServicePayoutBatchesQuery,
  useMarkPayoutBatchPaidMutation,
  useProviderTaxExportLazyQuery,
  useServicePayoutBatchesCsvLazyQuery,
} from '../../services/serviceService/__generated__/queries.generated';
import { authStore } from '../../../store/authStore';
import {
  downloadTextFile,
  lastPayoutWeek,
  payoutBatchState,
  payoutCsvFileName,
  payoutMoney,
  payoutTotals,
  payoutWeekLabel,
  payoutWeekLabelFromMonday,
  recentPayoutWeeks,
  type PayoutBatch,
} from '../ServiceProviderPage/payouts';
import { stayErrorMessage } from '../StayHostPage/stayErrors';

const ALL_WEEKS = 'all';

function MarkPaidModal({ batch, onClose }: { batch: PayoutBatch; onClose: () => void }) {
  const [markPaid, { loading }] = useMarkPayoutBatchPaidMutation({
    refetchQueries: ['AdminServicePayoutBatches'],
  });
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!reference.trim()) {
      setError('Enter the transfer reference');
      return;
    }
    setError(null);
    try {
      await markPaid({ variables: { batchId: batch.id, reference: reference.trim() } });
      toast.success('Marked paid');
      onClose();
    } catch (e) {
      setError(stayErrorMessage(e, 'Could not mark it paid'));
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Mark payout paid">
      <Stack $gap={4} data-testid="payout-mark-paid-modal">
        <Strong>Mark this payout paid?</Strong>
        <Muted>
          {batch.providerName}, {payoutWeekLabel(batch.periodStart, batch.periodEnd)}:{' '}
          {payoutMoney(batch.amount)} for {batch.jobCount} job{batch.jobCount === 1 ? '' : 's'}.
          {batch.amount < 0
            ? ' Refunds were more than the jobs, so the Provider owes this. Enter a reference once it is settled.'
            : ' Send the transfer first, then enter its reference.'}{' '}
          This cannot be undone.
        </Muted>
        <Field label="Transfer reference" htmlFor="payout-reference" required>
          <Input
            id="payout-reference"
            maxLength={255}
            value={reference}
            onChange={e => setReference(e.target.value)}
            data-testid="payout-reference"
          />
        </Field>
        {error && (
          <Alert tone="error" title="Not saved">
            {error}
          </Alert>
        )}
        <InlineRow>
          <Button
            type="button"
            text="Mark paid"
            onClick={submit}
            isLoading={loading}
            dataTestId="payout-mark-paid-save"
          />
          <Button type="button" theme="text" text="Cancel" onClick={onClose} />
        </InlineRow>
      </Stack>
    </Modal>
  );
}

/** D28: last year's Provider totals and tax info for the 1099s (platform owner). */
function TaxExportCard() {
  const [year, setYear] = useState(String(Math.max(2026, DateTime.now().year - 1)));
  const [exportCsv, { loading }] = useProviderTaxExportLazyQuery({ fetchPolicy: 'network-only' });

  const run = async () => {
    const value = Number(year);
    if (!Number.isInteger(value) || value < 2026 || value > DateTime.now().year) {
      toast.error('Pick a year from 2026 to this year');
      return;
    }
    const { data, error } = await exportCsv({ variables: { year: value } });
    if (error || !data?.providerTaxExport) {
      toast.error(stayErrorMessage(error, 'Could not make the export'));
      return;
    }
    window.open(data.providerTaxExport, '_blank', 'noopener');
  };

  return (
    <Card>
      <CardHeader
        title="1099 export"
        description="What each Provider was paid in a year, with their W-9 details and full tax ID, as a CSV. The link works for 15 minutes. Keep the file private."
      />
      <CardBody>
        <InlineRow>
          <Input
            aria-label="Year"
            inputMode="numeric"
            style={{ maxWidth: 120 }}
            value={year}
            onChange={e => setYear(e.target.value)}
          />
          <Button
            type="button"
            theme="secondary"
            text="Download"
            onClick={run}
            isLoading={loading}
            dataTestId="payout-tax-export"
          />
        </InlineRow>
      </CardBody>
    </Card>
  );
}

/**
 * Weekly Provider payouts (spec 019 W24, D23), brand admin: each Provider's
 * batch of a week, a CSV of the week for the bank transfers, and "Mark paid"
 * with the transfer reference once the money is sent.
 */
export default function AdminServicePayoutsPage() {
  const { user } = useSnapshot(authStore);
  const isPlatformOwner = Boolean((user as any)?.isPlatformOwner);
  const [week, setWeek] = useState<string>(() => lastPayoutWeek());
  const periodStart = week === ALL_WEEKS ? null : week;
  const query = useAdminServicePayoutBatchesQuery({
    variables: { periodStart },
    fetchPolicy: 'network-only',
  });
  const [fetchCsv, { loading: csvLoading }] = useServicePayoutBatchesCsvLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [paying, setPaying] = useState<PayoutBatch | null>(null);
  const batches = query.data?.adminServicePayoutBatches ?? [];
  const totals = payoutTotals(batches);

  const downloadCsv = async () => {
    if (!periodStart) return;
    const { data, error } = await fetchCsv({ variables: { periodStart } });
    if (error || data?.servicePayoutBatchesCsv == null) {
      toast.error(stayErrorMessage(error, 'Could not make the CSV'));
      return;
    }
    downloadTextFile(payoutCsvFileName(periodStart), data.servicePayoutBatchesCsv);
  };

  return (
    <PageContainer width="lg" canvas data-testid="admin-service-payouts">
      <PageHeader
        eyebrow="Admin"
        title="Service payouts"
        description="Every Monday each Provider gets a batch of last week's confirmed jobs, less refunds decided that week. Send the transfers, then mark each batch paid."
        actions={
          <Button
            type="button"
            theme="secondary"
            text="Download CSV"
            onClick={downloadCsv}
            isLoading={csvLoading}
            isDisabled={!periodStart || batches.length === 0}
            dataTestId="payout-csv"
          />
        }
      />
      <Stack>
        <Field label="Week" htmlFor="payout-week">
          <Select
            id="payout-week"
            value={week}
            onChange={e => setWeek(e.target.value)}
            style={{ maxWidth: 320 }}
            data-testid="payout-week"
          >
            {recentPayoutWeeks().map(monday => (
              <option key={monday} value={monday}>
                {payoutWeekLabelFromMonday(monday)}
              </option>
            ))}
            <option value={ALL_WEEKS}>All weeks</option>
          </Select>
        </Field>
        {query.loading && !query.data && <LoadingBlock label="Loading payouts" />}
        {query.error && (
          <Alert tone="error" title="Could not load payouts">
            {stayErrorMessage(query.error)}
          </Alert>
        )}
        {query.data && (
          <>
            <StatGrid>
              <StatCard label="Total" value={payoutMoney(totals.total)} />
              <StatCard
                label="Still to send"
                value={payoutMoney(totals.unpaid)}
                hint={`${totals.unpaidCount} batch${totals.unpaidCount === 1 ? '' : 'es'}`}
              />
            </StatGrid>
            <Card>
              {batches.length === 0 ? (
                <EmptyState
                  title="No payouts"
                  description="No Provider had a confirmed job or a refund in this week."
                />
              ) : (
                <TableWrap>
                  <Table data-testid="admin-payouts-table">
                    <THead>
                      <TR>
                        <TH>Provider</TH>
                        <TH>Week</TH>
                        <TH $align="right">Jobs</TH>
                        <TH $align="right">Amount</TH>
                        <TH>Status</TH>
                        <TH>Reference</TH>
                        <TH />
                      </TR>
                    </THead>
                    <TBody>
                      {batches.map(batch => {
                        const state = payoutBatchState(batch);
                        return (
                          <TR key={batch.id}>
                            <TD>{batch.providerName}</TD>
                            <TD>{payoutWeekLabel(batch.periodStart, batch.periodEnd)}</TD>
                            <TD $align="right">{batch.jobCount}</TD>
                            <TD $align="right">{payoutMoney(batch.amount)}</TD>
                            <TD>
                              <Badge tone={state.tone}>{state.label}</Badge>
                              {batch.paidAt && (
                                <Muted $size="sm" style={{ display: 'block' }}>
                                  {DateTime.fromISO(batch.paidAt).toFormat('LLL d, yyyy')}
                                </Muted>
                              )}
                            </TD>
                            <TD>{batch.reference ?? '—'}</TD>
                            <TDActions>
                              {!batch.paidAt && batch.amount !== 0 && (
                                <Button
                                  type="button"
                                  size="sm"
                                  theme="secondary"
                                  text="Mark paid"
                                  onClick={() => setPaying(batch)}
                                  dataTestId={`payout-mark-paid-${batch.id}`}
                                />
                              )}
                            </TDActions>
                          </TR>
                        );
                      })}
                    </TBody>
                  </Table>
                </TableWrap>
              )}
            </Card>
          </>
        )}
        {isPlatformOwner && <TaxExportCard />}
      </Stack>
      {paying && <MarkPaidModal batch={paying} onClose={() => setPaying(null)} />}
    </PageContainer>
  );
}
